import { Prof, select } from "gamedata/GameData";
import { Fragment, type ReactElement } from "react";
import { Link, useLoaderData } from "react-router";
import { z } from "zod";
import { InputMD } from "~/components/form/InputMd";
import { ClassLink } from "~/components/npc/ClassLink";
import { ProfLink } from "~/components/prof/ProfLink";
import { QuestLink } from "~/components/quest/QuestLink";
import { NotFoundInfo } from "~/components/site/NotFoundInfo";
import { SP } from "~/components/site/SP";
import { SkillLink } from "~/components/skill/SkillLink";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Facts } from "~/components/ui/Facts";
import { Icon } from "~/components/ui/Icon";
import { InfoBox } from "~/components/ui/InfoBox";
import { PageTitle } from "~/components/ui/PageTitle";
import { renderTableTr, Table } from "~/components/ui/Table";
import { GameVer, tt, useT, useTurl } from "~/util/Lang";
import type { MetaFunc, OurLoaderArgs } from "~/util/Loaders";
import { logPageView } from "~/util/stats";
import { homePage } from "~/util/titles";

import css from "./classes.module.css";

type ProfAlt = {
  name: string;
  profName: string;
  gver: GameVer;
};

async function loadData(
  la: OurLoaderArgs,
): Promise<
  | { status: "404"; alts: ProfAlt[] }
  | { status: "ok"; data: Awaited<ReturnType<typeof makeData>> }
> {
  const t = tt.bind(null, la.realm.lang);
  const params = Params.parse(la.params);
  const prof = la.gd.profs.get(params.classId);

  if (prof == null) {
    la.setStatusCode(404);
    const alts: ProfAlt[] = [];
    for (const altGD of Object.values(la.gd.all_gd!)) {
      const altProf = altGD.profs.get(params.classId);
      if (altProf) {
        alts.push({
          name: t(altProf.name),
          profName: altProf.profName,
          gver: altGD.gver,
        });
      }
    }
    return { status: "404", alts };
  }

  return { status: "ok", data: await makeData(la, prof) };
}

const Params = z.object({ classId: z.string() });
async function makeData(la: OurLoaderArgs, class_: Prof) {
  // Log
  logPageView(la);
  //

  const t = tt.bind(null, la.realm.lang);

  if (class_ == null) {
    throw Response.json(
      { msg: t({ en: "Class not found.", ru: "Класс не найден." }) },
      { status: 404 },
    );
  }

  const data = {
    prof: select(class_, {
      id: true,
      profName: true,
      name: t,
      skills: {
        select: {
          autoGet: true,
          cardName: true,
          skill: {
            id: true,
            name: t,
            desc: t,
            level: true,
            icon: true,
            skillName: true,
            mp_consume1: true,
            mp_consume2: true,
            hp_consume: true,
          },
          sp: true,
          books: {
            select: {
              select: { name: t, itemName: true, icon: true },
            },
          },
          level: true,
        },
      },
      prev: { id: true, profName: true, name: t },
      next: { select: { id: true, profName: true, name: t } },
      trainingLevels: { select: true },
      quests: {
        select: {
          name: t,
          id: true,
          uname: true,
        },
      },
    }),
  };
  return data;
}

type LoaderData = Awaited<ReturnType<typeof loader>>;

export const loader = async (_la: OurLoaderArgs) => {
  const data = await loadData(_la);
  return { ...data, authUser: _la.req.sess?.user };
};

export const meta: MetaFunc<LoaderData> = (cx) => {
  if (cx.data.status === "404") {
    return {
      title: "Class not found - " + homePage(cx.realm),
    };
  }

  const name = cx.data.data.prof.name;
  return {
    title: `${name} - Classes - ${homePage(cx.realm)}`,
    description: `All skills ${name}`,
    ogTitle: `Class: ${name}`,
  };
};

export default function ProfPage() {
  const loaderData = useLoaderData<typeof loader>();
  const t = useT();
  const turl = useTurl();
  const classesTitle = t({ en: "Classes", ru: "Классы" });

  if (loaderData.status === "404") {
    return (
      <NotFoundInfo
        title={t({ en: "Location not found", ru: "Локация не найден" })}
        altsTitle={t({
          en: "This location exists in other gave versions:",
          ru: "Эта локация доступна в других версиях игры:",
        })}
        alts={loaderData.alts.map((altProf) => ({
          gver: altProf.gver,
          node: <ClassLink prof={altProf} />,
        }))}
      />
    );
  }

  const data = loaderData.data;

  const breadcrumbList = [
    { title: classesTitle, url: turl("/classes") },
    {
      title: data.prof.name,
      url: turl(`/classes/${data.prof.profName}`),
    },
  ];

  const rows: Array<(typeof data.prof.skills)[number] | ReactElement> = [];
  let lastReqLevel = -1;
  for (const skill of data.prof.skills) {
    if (skill.level !== lastReqLevel) {
      rows.push(
        renderTableTr(
          { key: "level-" + skill.level },
          <td colSpan={4} className={css.levelTd}>
            {t({ en: "Level", ru: "Уровень" })} {skill.level}
          </td>,
        ),
      );
      lastReqLevel = skill.level;
    }
    rows.push(skill);
  }

  return (
    <div>
      <div className="content">
        <Breadcrumb items={breadcrumbList} />
        <div className="columns">
          <div className="column is-three-quarters">
            <PageTitle>{data.prof.name}</PageTitle>

            {data.prof.quests.length > 0 && (
              <div style={{ marginTop: 10 }}>
                {data.prof.quests.length > 1
                  ? t({
                      en: "Class Transfer Quests",
                      ru: "Задания на смену класса",
                    })
                  : t({
                      en: "Class Transfer Quest",
                      ru: "Задание на смену класса",
                    })}
                :{" "}
                {data.prof.quests.map((quest, i) => (
                  <Fragment key={quest.id}>
                    {i > 0 ? ", " : null}
                    <QuestLink quest={quest} />
                  </Fragment>
                ))}
              </div>
            )}
          </div>
          <div className="column">
            <InfoBox
              title={t({
                en: "Class Facts",
                ru: "Краткая информация",
              })}
            >
              <Facts
                facts={[
                  {
                    name: t({ en: "Previous", ru: "Предыдущий класс" }),
                    value: data.prof.prev && <ProfLink data={data.prof.prev} />,
                    show: !!data.prof.prev,
                  },
                  {
                    name: t({ en: "Next", ru: "Следующий класс" }),
                    value: data.prof.next.map((next, i) => (
                      <Fragment key={i}>
                        {i > 0 ? ", " : null}
                        <ProfLink data={next} />
                      </Fragment>
                    )),
                    show: data.prof.next.length > 0,
                  },
                ]}
              />
              <InputMD value={`:prof[${data.prof.profName}]`} />
            </InfoBox>
          </div>
        </div>
      </div>
      <div style={{ marginTop: 20, overflowX: "auto" }}>
        <Table
          data={rows}
          id={(_skill, index) => index}
          cols={[
            {
              title: "",
              cell: ({ skill }) => <Icon icon={skill.icon} alt={skill.name} />,
              thPadding: "0",
              padding: "8px 0 8px 8px",
              width: 32 + 12,
            },
            {
              title: t({ en: "Name", ru: "Имя" }),
              cell: (data) => (
                <>
                  <div
                    style={{
                      display: "flex",
                      columnGap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <SkillLink
                      data={{ ...data.skill, cardName: data.cardName }}
                      showIcon={false}
                    />
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#9999FF",
                      }}
                    >
                      {data.skill.level} {t({ en: "Level", ru: "Уровень" })}
                    </div>
                  </div>
                  <div style={{ fontSize: "14px" }}>{data.skill.desc}</div>
                </>
              ),
            },
            {
              title: t({ en: "MP/HP", ru: "МП/ХП" }),
              cell: ({ skill }) =>
                skill.mp_consume1 + skill.mp_consume2 + skill.hp_consume >
                  0 && (
                  <span style={{ fontSize: 14 }}>
                    <span style={{ color: "#008cff" }}>
                      {Math.round(skill.mp_consume1 + skill.mp_consume2)}
                    </span>
                    <span style={{ padding: "0 4px" }}>/</span>
                    <span style={{ color: "red" }}>
                      {Math.round(skill.hp_consume ?? 0)}
                    </span>
                  </span>
                ),
            },
            {
              title: t({ en: "Required", ru: "Требуется" }),
              cell: ({ sp, books }) => (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    rowGap: 8,
                  }}
                >
                  {books.map((item, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "start",
                        columnGap: 5,
                        fontSize: 14,
                        lineHeight: "1",
                      }}
                    >
                      <Icon icon={item[0].icon} size={16} />
                      <Link to={turl(`/items/${item[0].itemName}`)}>
                        {item[0].name}
                      </Link>
                    </div>
                  ))}
                  <SP count={sp} />
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}
